import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './global.css';
import App from 'App.js';

const {
  REACT_APP_SENTRY_ENVIRONMENT: sentryEnvironment,
  REACT_APP_PACKAGE_VERSION,
  REACT_APP_PACKAGE_NAME,
} = process.env;

const sentryReleaseVersion = `${REACT_APP_PACKAGE_NAME}@${REACT_APP_PACKAGE_VERSION}`;

/** Chrome quirk: harmless; PDF/layout code often triggers it. Match loosely for locale/punctuation variants. */
const isResizeObserverLoopMessage = msg => {
  if (typeof msg !== 'string') {
    return false;
  }
  const m = msg.trim();
  return (
    /resizeobserver\s+loop\s+limit\s+exceeded/i.test(m) ||
    /resizeobserver\s+loop\s+completed\s+with\s+undelivered\s+notifications/i.test(m)
  );
};

// Prevent the ResizeObserver loop warning at the source.
// Some third-party widgets (notably PDF viewers) can trigger RO callbacks during layout thrash.
// Wrapping callbacks in rAF reduces feedback loops and avoids CRA's blocking overlay in dev.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  const NativeResizeObserver = window.ResizeObserver;
  if (typeof NativeResizeObserver === 'function') {
    window.ResizeObserver = class PatchedResizeObserver extends NativeResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => callback(entries, observer));
          } else {
            setTimeout(() => callback(entries, observer), 0);
          }
        });
      }
    };
  }
}

const messageFromErrorEvent = event => {
  if (!event) {
    return '';
  }
  if (typeof event.message === 'string' && event.message) {
    return event.message;
  }
  const err = event.error;
  if (typeof err === 'string') {
    return err;
  }
  if (err && typeof err.message === 'string') {
    return err.message;
  }
  return '';
};

const messageFromRejectionEvent = event => {
  const r = event?.reason;
  if (typeof r === 'string') {
    return r;
  }
  if (r && typeof r.message === 'string') {
    return r.message;
  }
  return '';
};

const suppressResizeObserverErrorEvent = event => {
  const msg = messageFromErrorEvent(event);
  if (!isResizeObserverLoopMessage(msg)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

const suppressResizeObserverRejection = event => {
  if (!isResizeObserverLoopMessage(messageFromRejectionEvent(event))) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

// Adobe's PDF viewer and responsive layout changes can trigger this browser-level
// warning in development, which CRA / webpack dev overlay surfaces as a runtime error.
// Register capture + bubble so the overlay (often bubble-phase) does not show it.
window.addEventListener('error', suppressResizeObserverErrorEvent, true);
window.addEventListener('error', suppressResizeObserverErrorEvent, false);

window.addEventListener('unhandledrejection', suppressResizeObserverRejection, true);
window.addEventListener('unhandledrejection', suppressResizeObserverRejection, false);

// Some dev overlays hook into window.onerror (not just addEventListener). Returning true
// prevents the browser's default error handling and can stop the overlay regardless of
// listener registration order.
const previousWindowOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  const msg =
    typeof message === 'string'
      ? message
      : typeof error?.message === 'string'
        ? error.message
        : '';
  if (isResizeObserverLoopMessage(msg)) {
    return true;
  }
  if (typeof previousWindowOnError === 'function') {
    return previousWindowOnError(message, source, lineno, colno, error) === true;
  }
  return false;
};

const previousUnhandledRejection = window.onunhandledrejection;
window.onunhandledrejection = event => {
  if (isResizeObserverLoopMessage(messageFromRejectionEvent(event))) {
    event?.preventDefault?.();
    return true;
  }
  if (typeof previousUnhandledRejection === 'function') {
    return previousUnhandledRejection(event) === true;
  }
  return false;
};

// CRA's runtime overlay can also trigger off console.error/console.warn output.
// Filter the specific ResizeObserver noise so it doesn't block dev workflow.
const originalConsoleError = console.error;
console.error = (...args) => {
  const first = args?.[0];
  const msg =
    typeof first === 'string'
      ? first
      : typeof first?.message === 'string'
        ? first.message
        : '';
  if (isResizeObserverLoopMessage(msg)) {
    return;
  }
  originalConsoleError(...args);
};

const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  const first = args?.[0];
  const msg =
    typeof first === 'string'
      ? first
      : typeof first?.message === 'string'
        ? first.message
        : '';
  if (isResizeObserverLoopMessage(msg)) {
    return;
  }
  originalConsoleWarn(...args);
};

// CRA uses react-error-overlay to show a blocking red screen on runtime errors in dev.
// It can falsely surface Chrome's benign ResizeObserver loop warning as a runtime error.
// Reconfigure the overlay to ignore that specific message.
if (process.env.NODE_ENV === 'development') {
  import('react-error-overlay')
    .then(overlay => {
      try {
        overlay.stopReportingRuntimeErrors();
      } catch (e) {
        // noop - already stopped or API unavailable
      }

      try {
        overlay.startReportingRuntimeErrors({
          onError: ({ error }) => {
            if (isResizeObserverLoopMessage(error?.message)) {
              return;
            }
            overlay.reportRuntimeError(error);
          },
        });
      } catch (e) {
        // noop - overlay API signature can vary across react-scripts versions
      }
    })
    .catch(() => {
      // ignore - overlay not present outside CRA dev
    });
}

if (sentryReleaseVersion && sentryEnvironment && sentryEnvironment !== 'local') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: sentryEnvironment,
    release: sentryReleaseVersion,
  });
}

const root = createRoot(document.getElementById('root'));
root.render(<App />);
